import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "motion/react";
import { Page, Screen, ScreenHeader, Section } from "../components/AppShell";
import { Button, IconButton, Skeleton, StateMessage } from "../components/ui";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Icon } from "../components/Icon";
import { useToast } from "../components/Toast";
import { MediaHero } from "../features/media/MediaHero";
import { CachedMedia } from "../features/media/CachedMedia";
import { AttachmentList } from "../features/media/AttachmentList";
import { useMediaRefresh } from "../features/media/useMediaRefresh";
import { useArtwork, useDeleteArtwork, useRestoreArtwork } from "../api/queries";
import { haptic } from "../telegram/haptics";
import { fadeUp, listItem, sectionStagger } from "../motion";
import { fieldLabels, type FieldLabel } from "../i18n/fieldLabels";
import { useT } from "../i18n/strings";
import { termLabel, pick } from "../i18n/labels";
import {
  formatDate,
  formatDimensions,
  formatMoney,
  isLatinScript,
  recordDir,
} from "../i18n/format";
import { messagesOf } from "../api/errors";
import type { Artwork, Attachment } from "../api/types";
import { MEDIA_RIGHTS_ENABLED } from "../features/artworks/flags";
import { PublicPageSection } from "../features/share/PublicPageSection";
import { useCatalogScope } from "../features/catalog/CatalogProvider";
import s from "../features/artworks/detail.module.css";

export default function Detail() {
  const t = useT();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  /** Which media file the hero is showing; null means the thumbnail, as loaded. */
  const [viewing, setViewing] = useState<string | null>(null);

  const query = useArtwork(id);
  const remove = useDeleteArtwork();
  const restore = useRestoreArtwork();
  const { isOwnCatalog } = useCatalogScope();

  const refresh = useCallback(() => void query.refetch(), [query]);
  const refreshMedia = useMediaRefresh(refresh);

  const primaryId = query.data?.attachments.find((file) => file.kind === "primary")?.id ?? null;
  useEffect(() => {
    setViewing(null);
  }, [id, primaryId]);

  const onDelete = () => {
    setConfirming(false);
    remove.mutate(id, {
      onSuccess: () => {
        haptic.success();
        navigate("/", { replace: true });
        toast.show(t.detail.deleted, {
          action: {
            label: t.actions.undo,
            run: () =>
              restore.mutate(id, {
                onSuccess: () => toast.show(t.detail.restored),
              }),
          },
        });
      },
      onError: (error) => toast.show(messagesOf(error)[0] ?? t.errors.title, { tone: "error" }),
    });
  };

  if (query.isPending) return <DetailSkeleton />;

  if (query.error || !query.data) {
    return (
      <Screen withTabs={false}>
        <ScreenHeader title="" />
        <StateMessage
          icon="alert"
          title={t.errors.title}
          body={query.error ? messagesOf(query.error)[0] : t.errors.notFound}
          action={
            <Button variant="secondary" onClick={() => navigate("/")}>
              {t.tabs.works}
            </Button>
          }
        />
      </Screen>
    );
  }

  const art = query.data;
  const primary = art.attachments.find((file) => file.kind === "primary") ?? null;
  // The media section in the order the form shows it: thumbnail first.
  const media = primary
    ? [primary, ...art.attachments.filter((file) => file.kind === "gallery")]
    : art.attachments.filter((file) => file.kind === "gallery");
  const shown = media.find((file) => file.id === viewing) ?? primary;
  const conditionFiles = art.attachments.filter((file) => file.kind === "condition_report");
  const evidenceFiles = art.attachments.filter((file) => file.kind === "authenticity_evidence");
  const latin = isLatinScript(art.title);

  const showOwnership = Boolean(art.ownership_status_detail) || hasValuation(art);
  const showCondition =
    Boolean(art.condition_status_detail) ||
    Boolean(art.condition_notes) ||
    conditionFiles.length > 0;
  const showProvenance = Boolean(art.provenance_text) || Boolean(art.provenance_confidence_detail);
  const showAuthenticity = Boolean(art.authenticity_notes) || evidenceFiles.length > 0;

  return (
    <Screen withTabs={false}>
      <ScreenHeader
        overlay
        actions={
          <>
            <IconButton
              name="edit"
              label={t.actions.edit}
              className="chrome-media"
              onClick={() => navigate(`/artwork/${art.id}/edit`)}
            />
          </>
        }
      />

      <MediaHero
        media={shown}
        // The shared element is the thumbnail growing out of its list row. While
        // another file is on screen it is not that picture, so it does not fly.
        layoutId={shown?.id === primary?.id && primary ? `hero-${primary.id}` : undefined}
        onExpired={refreshMedia}
      />

      {media.length > 1 && (
        <div className={s.strip}>
          {media.map((file) => (
            <button
              key={file.id}
              type="button"
              className={[s.stripTile, file.id === shown?.id && s.stripTileOn]
                .filter(Boolean)
                .join(" ")}
              aria-pressed={file.id === shown?.id}
              aria-label={file.original_filename || file.mime_type}
              onClick={() => {
                haptic.tap();
                setViewing(file.id);
              }}
            >
              <CachedMedia
                media={file}
                prefer="thumbnail"
                className={s.stripPreview}
                fallback={<Icon name="image" size={20} />}
              />
            </button>
          ))}
        </div>
      )}

      <motion.div variants={sectionStagger} initial="hidden" animate="show">
        <motion.div className={s.titleBlock} variants={listItem}>
          <h1
            className={[s.title, latin && s.titleLatin, "bidi"].filter(Boolean).join(" ")}
            dir={recordDir(art.title || t.detail.untitled)}
          >
            {art.title || t.detail.untitled}
          </h1>
          <p className={s.subtitle}>
            <span
              className="bidi"
              dir={recordDir(art.artist_detail?.name || t.detail.unknownArtist)}
            >
              {art.artist_detail?.name || t.detail.unknownArtist}
            </span>
            {art.year_display && (
              <>
                <span className={s.dot} />
                <span className="mono">{art.year_display}</span>
              </>
            )}
            {art.type_detail && (
              <>
                <span className={s.dot} />
                <span>{termLabel(art.type_detail)}</span>
              </>
            )}
          </p>
        </motion.div>

        <Page>
          <motion.div variants={listItem}>
            <Section title={t.form.sections.classification} icon="type">
              <div className={s.rows}>
                <Row label={fieldLabels.type}>{termLabel(art.type_detail)}</Row>
                <Row label={fieldLabels.subtype}>{termLabel(art.subtype_detail)}</Row>
                <Row label={fieldLabels.mediums}>
                  {art.mediums_detail.length > 0 && (
                    <span className={s.tagList}>
                      {art.mediums_detail.map((term) => (
                        <span key={term.id} className={s.tag}>
                          {termLabel(term)}
                        </span>
                      ))}
                    </span>
                  )}
                </Row>
                <Row label={fieldLabels.style}>{termLabel(art.style_detail)}</Row>
                <Row label={fieldLabels.curatorial_notes}>
                  {art.curatorial_notes && <p className={s.notes}>{art.curatorial_notes}</p>}
                </Row>
              </div>
            </Section>
          </motion.div>

          {(hasDimensions(art) ||
            art.is_life_size ||
            art.edition_number !== null ||
            (art.parts?.length ?? 0) > 0) && (
            <motion.div variants={listItem}>
              <Section title={pick(fieldLabels.dimensions)} icon="dimensions">
                <div className={s.rows}>
                  <Row label={fieldLabels.artworkDimensions}>
                    <Centimetres
                      text={formatDimensions(
                        art.artwork_width_cm,
                        art.artwork_height_cm,
                        art.artwork_depth_cm,
                      )}
                    />
                  </Row>
                  <Row label={fieldLabels.frameDimensions}>
                    <Centimetres
                      text={formatDimensions(
                        art.frame_width_cm,
                        art.frame_height_cm,
                        art.frame_depth_cm,
                      )}
                    />
                  </Row>
                  <Row label={fieldLabels.is_life_size}>
                    {art.is_life_size ? pick(fieldLabels.is_life_size) : null}
                  </Row>
                  <Row label={fieldLabels.edition}>
                    {art.edition_number !== null && art.edition_size !== null && (
                      <span className="mono">
                        {art.edition_number}/{art.edition_size}
                      </span>
                    )}
                  </Row>
                  {(art.parts ?? []).map((part, index) => (
                    <Row
                      key={part.id}
                      label={{
                        fa: part.label || `${t.form.partLine(index + 1)}`,
                        en: part.label || t.form.partLine(index + 1),
                      }}
                    >
                      <Centimetres
                        text={formatDimensions(part.width_cm, part.height_cm, part.depth_cm)}
                      />
                    </Row>
                  ))}
                </div>
              </Section>
            </motion.div>
          )}

          {isOwnCatalog && (
            <motion.div variants={listItem}>
              <PublicPageSection artwork={art} />
            </motion.div>
          )}

          {showOwnership && (
            <motion.div variants={listItem}>
              <Section title={t.form.sections.ownership} icon="ownership">
                <div className={s.rows}>
                  <Row label={fieldLabels.ownership_status}>
                    {art.ownership_status_detail && (
                      <StatusChip text={termLabel(art.ownership_status_detail)} />
                    )}
                  </Row>
                </div>
                {hasValuation(art) && (
                  <div className={s.valuation}>
                    <Amount
                      label={pick(fieldLabels.price_asking)}
                      value={art.price_asking}
                      code={art.currency_detail?.code}
                    />
                    <Amount
                      label={pick(fieldLabels.price_sold)}
                      value={art.price_sold}
                      code={art.currency_detail?.code}
                    />
                    <Amount
                      label={pick(fieldLabels.price_estimate)}
                      value={art.price_estimate}
                      code={art.currency_detail?.code}
                    />
                  </div>
                )}
              </Section>
            </motion.div>
          )}

          {showCondition && (
            <motion.div variants={listItem}>
              <Section title={pick(fieldLabels.condition_status)} icon="condition">
                {art.condition_status_detail && (
                  <StatusChip text={termLabel(art.condition_status_detail)} />
                )}
                {art.condition_notes && <p className={s.notes}>{art.condition_notes}</p>}
                {conditionFiles.length > 0 && <AttachmentList files={conditionFiles} />}
              </Section>
            </motion.div>
          )}

          {showProvenance && (
            <motion.div variants={listItem}>
              <Section title={pick(fieldLabels.provenance_text)} icon="provenance">
                {art.provenance_text && <p className={s.notes}>{art.provenance_text}</p>}
                {art.provenance_confidence_detail && (
                  <div className={s.rows}>
                    <Row label={fieldLabels.provenance_confidence}>
                      <StatusChip text={termLabel(art.provenance_confidence_detail)} />
                    </Row>
                  </div>
                )}
              </Section>
            </motion.div>
          )}

          {showAuthenticity && (
            <motion.div variants={listItem}>
              <Section title={pick(fieldLabels.authenticity)} icon="authenticity">
                {art.authenticity_notes && <p className={s.notes}>{art.authenticity_notes}</p>}
                {evidenceFiles.length > 0 && <AttachmentList files={evidenceFiles} />}
                {/* A certificate on file is evidence. It is not a verdict, and no
                  copy in this app is allowed to imply that it is. */}
                <p className={s.evidenceNote}>
                  <Icon name="info" size={16} />
                  <span>{t.detail.authenticityNote}</span>
                </p>
              </Section>
            </motion.div>
          )}

          {MEDIA_RIGHTS_ENABLED && primary && (
            <motion.div variants={listItem}>
              <Section title={t.form.sections.media} icon="image">
                <div className={s.rows}>
                  <Row label={fieldLabels.rights}>
                    <RightsLabel media={primary} />
                  </Row>
                  <Row label={fieldLabels.rights_note}>{primary.rights_note}</Row>
                  <Row label={fieldLabels.access_level}>
                    <StatusChip text={t.media.accessLevel[primary.access_level]} />
                  </Row>
                </div>
                {/* The API serves every attachment the owner can address, whatever the
                    level says. Reporting it is honest; drawing a lock would not be. */}
                <p className={s.evidenceNote}>
                  <Icon name="info" size={16} />
                  <span>{t.detail.accessNote}</span>
                </p>
              </Section>
            </motion.div>
          )}

          <div className={s.footMeta}>
            {/* The label is prose and the code is a value: only the value runs
                LTR, or the colon lands on the wrong side of a Persian label. */}
            {art.inventory_code && (
              <span>
                {pick(fieldLabels.inventory_code)}:{" "}
                <span className={s.idLine}>{art.inventory_code}</span>
              </span>
            )}
            <span>
              ID <span className={s.idLine}>{art.id}</span>
            </span>
            <span>
              {pick(fieldLabels.created_at)}: {formatDate(art.created_at)}
            </span>
            <span>
              {pick(fieldLabels.updated_at)}: {formatDate(art.updated_at)}
            </span>
          </div>

          <div className={s.deleteRow}>
            <Button
              variant="destructive"
              icon="delete"
              onClick={() => setConfirming(true)}
              style={{ width: "100%" }}
            >
              {t.actions.delete}
            </Button>
          </div>
        </Page>
      </motion.div>

      <ConfirmDialog
        open={confirming}
        title={t.actions.confirmDelete}
        body={t.detail.deletePrompt}
        onConfirm={onDelete}
        onCancel={() => setConfirming(false)}
      />
    </Screen>
  );
}

/* ------------------------------------------------------------------ bits -- */

/** Renders nothing when the field is empty; a catalog of «—» reads as broken. */
function Row({ label: fieldLabel, children }: { label: FieldLabel; children: ReactNode }) {
  const empty =
    children === null || children === undefined || children === false || children === "";
  if (empty) return null;
  return (
    <div className={s.row}>
      <span className={s.rowLabel}>{pick(fieldLabel)}</span>
      <span className={s.rowValue}>{children}</span>
    </div>
  );
}

/** A row written before the licence enum existed still holds its free text. */
function RightsLabel({ media }: { media: Attachment }) {
  const t = useT();
  if (!media.rights) return null;
  return <>{t.media.rightsLicense[media.rights] ?? media.rights}</>;
}

function Centimetres({ text }: { text: string }) {
  const t = useT();
  if (!text) return null;
  return (
    <span>
      <span className="mono">{text}</span> <span className={s.rowValueMuted}>{t.form.unit}</span>
    </span>
  );
}

function StatusChip({ text }: { text: string }) {
  return (
    <span className={`${s.statusChip} paper`}>
      <span className={s.statusDot} />
      {text}
    </span>
  );
}

function Amount({
  label: name,
  value,
  code,
}: {
  label: string;
  value: number | null;
  code?: string;
}) {
  if (value === null) return null;
  return (
    <div className={s.valuationRow}>
      <span className={s.valuationLabel}>{name}</span>
      <span className={s.valuationAmount}>{formatMoney(value, code)}</span>
    </div>
  );
}

function hasDimensions(art: Artwork): boolean {
  return [
    art.artwork_width_cm,
    art.artwork_height_cm,
    art.artwork_depth_cm,
    art.frame_width_cm,
    art.frame_height_cm,
    art.frame_depth_cm,
  ].some((value) => value !== null);
}

function hasValuation(art: Artwork): boolean {
  return [art.price_asking, art.price_sold, art.price_estimate].some((v) => v !== null);
}

function DetailSkeleton() {
  return (
    <Screen withTabs={false}>
      <motion.div variants={fadeUp} initial="hidden" animate="show">
        <Skeleton height={280} radius="0" />
        <Page>
          <div style={{ display: "grid", gap: "var(--space-3)", paddingBlock: "var(--space-5)" }}>
            <Skeleton height={26} width="70%" radius="4px" />
            <Skeleton height={16} width="45%" radius="4px" />
            <Skeleton height={120} radius="var(--radius-card)" />
            <Skeleton height={120} radius="var(--radius-card)" />
          </div>
        </Page>
      </motion.div>
    </Screen>
  );
}
